<script lang="ts">
    import { faEdit, faInfinity, faMoneyBill, faPlusCircle, faUser, faWindowClose, faPrint, faTrash } from '@fortawesome/free-solid-svg-icons'
    import { onMount } from 'svelte/internal'
    import Content from '../../_common/content.svelte'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import Popover from '../../_common/popover.svelte'
    import { GridBadge, GridBadgeClassEnum, GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../../_common/z/zgrid'
    import Zgrid from '../../_common/z/zgrid.svelte'
    // o <Zgrid> já se troca sozinho pelo Zgrid2; daqui vêm só os acts, porque "active" e o
    // type "date" não existem no GridButton do zgrid.ts
    import { GridButton as Grid2Button } from '../../_common/z/zgrid2'
    import Financial_4BillsForm from './financial-4-bills-form.svelte'
    import type { BillEntity } from '../../../modules/financialApi/bills/bill'
    import dayjs from 'dayjs'
    import { EntryTypeEnum } from '../../../modules/financialApi/entries/entry.enum'
    import Financial_4BillsFormPayment from './financial-4-bills-form-payment.svelte'
    import Financial_4BillsRecurring from './financial-4-bills-recurring.svelte'
    import _isToday from 'dayjs/plugin/isToday'
    import TemplateMobile from '../../_common/template-mobile.svelte'
    import { FormTemplateTypeEnum } from '../../../modules/toolsApi/forms/form-templates/form-template-item-type.enum'
    import { printFormV2 } from '../../customer-service-v2/forms/form-v2-print.helper'
    dayjs.extend(_isToday)

    let show = false
    let entities: BillEntity[] = []

    let dates: { from: Date; to: Date } = null
    function showDate() {
        dates = window.api.utils.dates.currentMonth()
    }

    onMount(async () => {
        await loadEntities()
    })

    function evaluateBadge(item: BillEntity): { label: string; class: string } {
        if (item.status === 100) {
            return {
                label: 'pago',
                class: GridBadgeClassEnum.badge_success,
            }
        }
        const isToday = dayjs(item.dueAt).isToday()
        if (isToday) {
            return {
                label: 'vence hoje',
                class: GridBadgeClassEnum.badge_alert,
            }
        }
        if (dayjs(item.dueAt).diff(new Date(), 'day') >= 0) {
            return {
                label: 'a pagar',
                class: GridBadgeClassEnum.badge_default,
            }
        }
        return {
            label: 'atrasado',
            class: GridBadgeClassEnum.badge_danger,
        }
    }

    async function loadEntities() {
        try {
            show = true
            const dt = {
                from: filterStatus === 'overdue' ? undefined : dates?.from || undefined,
                to: dates?.to || undefined,
                status: filterStatus || undefined,
                creditDebit: 'debit',
            }
            entities = await window.api.financials.bills.getAll(dt)
            entities = await window.api.utils.utils.assignPersonsFromObject(entities)
            loadGrid()
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        // grid
        grid = new GridClass()
        // grid head
        grid.title = 'Contas a Pagar'
        grid.subtitle = ''
        grid.fontSize = '15px'
        // filtros no grupo segmentado; "Por data" abre o seletor de período; os dois botões vão pro cabeçalho
        const filters: ['recent' | 'overdue', string][] = [
            ['recent', 'Recentes'],
            ['overdue', 'Atrasadas'],
        ]
        for (const [type, label] of filters) {
            grid.acts.push(new Grid2Button({ label, active: filterCurrent === type, callback: () => loadFromFilter(type) }))
        }
        grid.acts.push(
            new Grid2Button({
                label: 'Por data',
                type: 'date',
                active: filterCurrent === 'date',
                dateFrom: filterCurrent === 'date' ? dates?.from : null,
                dateTo: filterCurrent === 'date' ? dates?.to : null,
                callback: (period: { from: Date; to: Date }) => loadFromPeriod(period),
            }),
        )
        grid.acts.push(new Grid2Button({ label: 'Pagamentos recorrentes', icon: faInfinity, callback: () => goNewRecurringBill() }))
        grid.acts.push(new Grid2Button({ label: 'Novo pagamento', icon: faPlusCircle, color: 'primary', callback: () => goNewBill() }))
        // seleção em massa só para quem pode excluir (mesma permissão do "Excluir" individual)
        const canRemove = window.store.token.permissions.financial.entries.write
        if (canRemove) {
            const btnRemoveSelected = new GridButton({ callback: removeSelected, icon: faTrash, label: 'Excluir selecionados' })
            grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.checkbox, bulkActions: [btnRemoveSelected] }))
        }
        grid.cols.push(new GridCol({ content: 'Status', type: GridColTypeEnum.badge }))
        grid.cols.push(new GridCol({ content: 'Origem', type: GridColTypeEnum.badge }))
        grid.cols.push(new GridCol({ content: 'Vencimento', type: GridColTypeEnum.date }))
        grid.cols.push(new GridCol({ content: 'Pago em', type: GridColTypeEnum.date }))
        grid.cols.push(new GridCol({ content: 'Descrição', type: GridColTypeEnum.string, maxLength: 45 }))
        grid.cols.push(new GridCol({ content: 'Recebedor', type: GridColTypeEnum.string }))
        grid.cols.push(new GridCol({ content: 'A pagar', type: GridColTypeEnum.float_2, showTotal: true }))
        grid.cols.push(new GridCol({ content: 'Pago', type: GridColTypeEnum.float_2, showTotal: true }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        // grid lines
        for (const item of entities) {
            const btnPerson = new GridButton({
                callback: () => window.person('show', item.person?.personId),
                icon: faUser,
                // só o 1º botão da linha fica à mostra; os demais vão pro menu "⋯", onde precisam de texto
                label: 'Pessoa',
                disabled: item.personId === null,
            })
            const btnOpen = new GridButton({
                callback: () => goShowBill(item.billId),
                icon: faEdit,
                label: 'Editar',
            })
            let btnDoc
            btnDoc = new GridButton({
                callback: () => openDocument(item.billId, item.personId || item.person?.personId),
                icon: faPrint,
                label: 'Documento',
            })
            let btnPay
            if (item.status === 95) {
                btnPay = new GridButton({
                    callback: () => goShowPayment(item.billId),
                    icon: faMoneyBill,
                    label: 'Pagar',
                })
            } else {
                btnPay = new GridButton({
                    callback: () => voidPayment(item.billId),
                    icon: faWindowClose,
                    label: 'Estornar',
                })
            }
            let description = item.description
            if (item.installmentTotal > 1) {
                description = '(' + item.installmentNumber + '/' + item.installmentTotal + ') ' + description
            }
            let line: GridLine = new GridLine()
            if (canRemove) line.slots.push({ content: item })
            line.slots.push({ content: new GridBadge(evaluateBadge(item)) })
            if (item.source === 'recurring') {
                line.slots.push({
                    content: new GridBadge({ class: GridBadgeClassEnum.badge_default, label: 'recorrente' }),
                })
            } else {
                line.slots.push({
                    content: new GridBadge({ class: GridBadgeClassEnum.badge_default, label: 'digitação' }),
                })
            }
            let btns = []
            btns.push(btnPay)
            btns.push(btnOpen)
            if (btnDoc) btns.push(btnDoc)
            btns.push(btnPerson)
            line.slots.push({ content: item.dueAt })
            line.slots.push({ content: item.paidAt })
            line.slots.push({ content: description })
            line.slots.push({ content: item.person?.name || '-' })
            line.slots.push({ content: item.value })
            line.slots.push({ content: item.status >= 100 ? item.paidValue : null })
            line.slots.push({ content: btns })
            grid.lines.push(line)
        }
    }

    function openDocument(billId: string, personId: string) {
        if (window.configs?.documentVersionLatest) {
            window.popover({
                act: 'customer-service-v2',
                data: {
                    view: false,
                    personId,
                    billId,
                },
                callback: checkPrintV2,
            })
        } else {
            window.popover({
                act: 'customer-service',
                data: {
                    view: false,
                    personId,
                    billId,
                    types: [FormTemplateTypeEnum.recibo, FormTemplateTypeEnum.contrato],
                },
                callback: printDocument,
            })
        }
    }

    function printDocument(data) {
        if (!data.formDataId) return
        if (!confirm('Deseja imprimir o documento?')) return
        setTimeout(() => {
            window.popover({
                act: 'customer-service',
                data: {
                    view: true,
                    formDataId: data.formDataId,
                },
            })
        }, 1000)
    }

    async function checkPrintV2(data) {
        if (!data.formV2FormId) return
        if (!confirm('Deseja imprimir o documento?')) return
        try {
            show = true
            await printFormV2(data.formV2FormId)
        } finally {
            show = false
        }
    }

    let billId = null
    let showBill = false
    function goShowBill(id: string) {
        billId = id
        showBill = true
    }

    async function voidPayment(id: string) {
        if (!confirm('Deseja mesmo estornar o pagamento desta conta?')) return
        await window.api.financials.bills.void(id)
        await loadEntities()
    }

    // ação em massa da coluna de seleção do grid — recebe as contas marcadas (visíveis) no grid
    async function removeSelected(items: BillEntity[]) {
        // ZanduPay e PDV são mantidos pela integração e não podem ser excluídos por aqui (nem individualmente)
        const removables = items.filter((item) => item.source !== 'zandupay' && item.source !== 'pos')
        const ignored = items.length - removables.length
        if (removables.length === 0) {
            window.alert('As contas selecionadas vêm do ZanduPay ou do PDV e não podem ser excluídas por aqui.')
            return
        }
        const paid = removables.filter((item) => item.status === 100).length
        let message = `Deseja mesmo excluir ${removables.length} conta(s) a pagar?`
        if (paid > 0) {
            message += `\n\n${paid} delas já foram pagas. Se o pagamento foi lançado em uma conta financeira, esse lançamento também será excluído da Movimentação e dos relatórios.`
        }
        if (ignored > 0) {
            message += `\n\n${ignored} conta(s) do ZanduPay ou do PDV não podem ser excluídas por aqui e serão ignoradas.`
        }
        message += '\n\nEsta ação não pode ser desfeita.'
        if (!confirm(message)) return

        let failed = 0
        try {
            show = true
            for (const item of removables) {
                try {
                    await window.api.financials.bills.remove(item.billId)
                } catch (error) {
                    failed++
                }
            }
        } finally {
            show = false
        }
        if (failed > 0) {
            window.error(`${failed} de ${removables.length} conta(s) não puderam ser excluídas. Tente novamente.`)
        }
        await loadEntities()
    }

    let showPayment = false
    function goShowPayment(id: string) {
        billId = id
        showPayment = true
    }

    function goNewBill() {
        billId = null
        showBill = true
    }

    function goCloseBill(update: boolean) {
        showBill = false
        showPayment = false
        billId = null
        if (update) loadEntities()
    }

    let filterStatus: 'recent' | 'overdue' | 'date' = 'recent'
    let filterCurrent = 'recent'
    function loadFromFilter(type) {
        filterCurrent = type
        if (type === 'recent') {
            dates = null
            filterStatus = 'recent'
        }
        if (type === 'overdue') {
            dates = null
            filterStatus = 'overdue'
        }
        if (type === 'date') {
            showDate()
            filterStatus = null
        }
        loadEntities()
    }

    // "Por data" no Zgrid2: o act type "date" já entrega o período escolhido no popup
    function loadFromPeriod(period: { from: Date; to: Date }) {
        filterCurrent = 'date'
        filterStatus = null
        dates = period
        loadEntities()
    }

    let recurringBillId = null
    let showRecurring = false
    function goCloseRecurring() {
        showRecurring = false
        recurringBillId = null
    }

    function goNewRecurringBill() {
        showRecurring = true
    }
</script>

{#if showBill}
    <Popover title="Novo pagamento" width="970px" on:close={() => goCloseBill(true)}>
        <Financial_4BillsForm {billId} creditDebit={EntryTypeEnum.debit} on:change={() => goCloseBill(true)}></Financial_4BillsForm>
    </Popover>
{/if}

{#if showPayment}
    <Popover title="Pagar" width="840px" on:close={() => goCloseBill(true)}>
        <Financial_4BillsFormPayment {billId} on:change={() => goCloseBill(true)}></Financial_4BillsFormPayment>
    </Popover>
{/if}

{#if showRecurring}
    <Popover title="Pagamentos Recorrentes" width="1300px" height="90vh" on:close={() => goCloseRecurring()}>
        <Financial_4BillsRecurring creditDebit={EntryTypeEnum.debit} on:close={() => goCloseRecurring()}></Financial_4BillsRecurring>
    </Popover>
{/if}

<Content>
    <!-- os filtros, o período e os dois botões são acts do grid (ver loadGrid), por isso não há barra própria aqui -->
    <Zgrid {grid} class="mb-5 mt-2" />
    <LoadSpinner {show} />
</Content>

<style>
</style>
